import { useState, useEffect } from 'react';

export const useUI = () => {
  const [theme, setTheme] = useState('dark');
  const [menuOpen, setMenuOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [dataDropdownOpen, setDataDropdownOpen] = useState(false);
  const [backtestingDropdownOpen, setBacktestingDropdownOpen] = useState(false);
  const [navVisible, setNavVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showBookDemo, setShowBookDemo] = useState(false);

  // Initialize theme from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Set dark theme on mobile
  useEffect(() => {
    if (window.innerWidth <= 800) {
      document.documentElement.setAttribute('data-theme', 'dark');
    }
  }, []);

  // Handle scroll events to show/hide navbar
  useEffect(() => {
    const handleScrollWithDebounce = () => {
      const currentScrollY = window.scrollY;
      const scrollThreshold = 50;
      
      if (currentScrollY < 100) {
        setNavVisible(true);
      } else if (Math.abs(currentScrollY - lastScrollY) > scrollThreshold) {
        setNavVisible(currentScrollY < lastScrollY);
        setLastScrollY(currentScrollY);
      }
    };
    
    window.addEventListener('scroll', handleScrollWithDebounce);
    
    return () => {
      window.removeEventListener('scroll', handleScrollWithDebounce);
    };
  }, [lastScrollY]);

  // Close dropdowns on outside click
  useEffect(() => {
    const closeDropdowns = () => {
      setAboutDropdownOpen(false);
      setDataDropdownOpen(false);
      setBacktestingDropdownOpen(false);
    };
    
    document.addEventListener('click', closeDropdowns);
    return () => document.removeEventListener('click', closeDropdowns);
  }, []);

  // Handle body class for mobile menu
  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add('nav-open');
    } else {
      document.body.classList.remove('nav-open');
    }
    
    return () => {
      document.body.classList.remove('nav-open');
    };
  }, [menuOpen]);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const toggleMenu = (value) => {
    if (value !== undefined) {
      setMenuOpen(value);
    } else {
      setMenuOpen(!menuOpen);
    }
  };

  const toggleAboutDropdown = (e) => {
    e.stopPropagation();
    setAboutDropdownOpen(!aboutDropdownOpen);
    
    if (!aboutDropdownOpen) {
      setDataDropdownOpen(false);
      setBacktestingDropdownOpen(false);
    }
  };

  const toggleDataDropdown = (e) => {
    e.stopPropagation();
    setDataDropdownOpen(!dataDropdownOpen);
    
    if (!dataDropdownOpen) {
      setAboutDropdownOpen(false);
      setBacktestingDropdownOpen(false);
    }
  };

  const toggleBacktestingDropdown = (e) => {
    e.stopPropagation();
    setBacktestingDropdownOpen(!backtestingDropdownOpen);
    
    if (!backtestingDropdownOpen) {
      setAboutDropdownOpen(false);
      setDataDropdownOpen(false);
    }
  };

  const toggleBookDemo = () => {
    setShowBookDemo(!showBookDemo);
  };

  const closeAllDropdowns = () => {
    setAboutDropdownOpen(false);
    setDataDropdownOpen(false);
    setBacktestingDropdownOpen(false);
  };

  return {
    theme,
    menuOpen,
    aboutDropdownOpen,
    dataDropdownOpen,
    backtestingDropdownOpen,
    navVisible,
    showBookDemo,
    toggleTheme,
    toggleMenu,
    toggleAboutDropdown,
    toggleDataDropdown,
    toggleBacktestingDropdown,
    toggleBookDemo,
    closeAllDropdowns,
  };
};
